import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Notification, PopoverBeta, PopoverContainer, PopoverTarget } from 'is24-corecss'
import { animateScroll as scroll } from 'react-scroll'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconS24InfoCircle24 } from '@is24/cosma-ui-icons'
import { InsertionWizardPageProps } from '../../../../@types/props-types'
import { FlowName } from '../../../../@types/flow-types'
import NavigationProgressBar from '../navigationProgressBar/NavigationProgressBar'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { getNavigationBarScreens } from '../../../../services/screen/screenService'
import { useFormState } from './useFormState'
import { PublicationStickyFooter } from './publicationBarContent/PublicationStickyFooter'
import { useAppSelector } from '../../../../app/hooks'
import { SaveType } from '../../../../@types/types'
import { isWebView } from '../../../../utils/navigation'
import { AiDataExtractionBanner } from '../aiDataExtractionBanner/AiDataExtractionBanner'
import { FeatureSwitch } from '../../../../utils/features/featureSwitches'
import { useFeatureSwitch } from '../../../../utils/features/FeatureSwitchContext'
import styles from './InsertionWizardPage.module.css'

const DRAFT_LISTING_DESCRIPTION_FLOW_NAMES: FlowName[] = [
  FlowName.PROPERTY_OWNER_APARTMENT_RENT,
  FlowName.PROPERTY_OWNER_HOUSE_RENT,
]

const InsertionWizardPage: React.FC<InsertionWizardPageProps> = ({
  flowInformation,
  screen,
  flowScreens,
  children: content,
  onNavigate,
  onSave,
  onPreview,
  doCloseStatusMessage,
  validationMessages,
  onSaveForLater,
}) => {
  const { t } = useTranslation()
  const location = useLocation()
  const { increaseSubmitCount } = useFormState()
  const messageFeature = useAppSelector((state: InitState) => state.messageFeature)
  const isSaving = useAppSelector((state: InitState) => state.insertionFeature.save === SaveType.SAVING)
  const { id, buildingId, unitId } = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification
  )
  const objectChooserShown = useAppSelector(
    (state: InitState) => state.flowFeature.objectChooserInformation.objectChooserShown
  )
  const history = useNavigate()
  const hasValidGeoCoordinates = useAppSelector((state: InitState) => state.addressFeature.hasValidGeoCoordinates)
  const isNativeMobileView = isWebView()
  const { isActive: isDraftListingEnabled } = useFeatureSwitch(FeatureSwitch.PPA_12286_DRAFT_LISTING)
  useEffect(() => scroll.scrollToTop(), [messageFeature.message])
  useEffect(() => {
    if (objectChooserShown) {
      const params = new URLSearchParams(window.location.search)
      buildingId && params.set('buildingId', buildingId)
      unitId && params.set('unitId', unitId)
      history(`${flowInformation.path}/${id}?${params.toString()}`)
    } else {
      history(`${flowInformation.path}/${id}${location.search}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, objectChooserShown])

  const isWaitingListFlow = flowInformation.options?.isWaitingListFlow
  const shouldShowNavigationProgressBar = !isNativeMobileView && !isWaitingListFlow
  const description = flowInformation.options?.customPageHeader?.descriptionKey
    ? t(flowInformation.options?.customPageHeader.descriptionKey)
    : undefined
  const shouldShowDraftListingDescription =
    isDraftListingEnabled && DRAFT_LISTING_DESCRIPTION_FLOW_NAMES.includes(flowInformation.name)

  return (
    <>
      <div className={`desk-five-twelfth grid grid-flex ${isWaitingListFlow ? 'waiting-list-centered-header' : ''}`}>
        {isWaitingListFlow && description && (
          <span className='grid-item font-body-medium-regular margin-top-s font-default-color'>{description}</span>
        )}
      </div>
      <div className={`grid ${isWaitingListFlow ? 'waiting-list-centered-grid' : ''}`}>
        {shouldShowNavigationProgressBar && (
          <div className={'lap-hide palm-hide'}>
            <NavigationProgressBar
              flowInformation={flowInformation}
              screens={getNavigationBarScreens(flowScreens)}
              screenNo={screen}
              validationMessages={{
                ...validationMessages,
                address: {
                  fields: {
                    ...validationMessages.address.fields!,
                    manualRegion: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualRegion : [],
                    manualCity: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualCity : [],
                    manualQuarter: !hasValidGeoCoordinates ? validationMessages.address.fields?.manualQuarter : [],
                  },
                  validated: validationMessages.address.validated,
                },
              }}
              onNavigate={onNavigate}
            />
          </div>
        )}
        <div
          className={`grid-item palm-one-whole lap-one-whole ${isWaitingListFlow ? '' : 'desk-padding-left-xxl lap-padding-left-l lap-padding-right-l'} mf-form ${isNativeMobileView ? 'desk-one-whole desk-padding-right-xxl palm-padding-bottom-xxxl' : 'desk-three-fourth'}`}
        >
          <div className='lap-margin-left-m lap-margin-right-m'>
            {messageFeature.message && (
              <div className='desk-one-half'>
                <Notification
                  data-testid='status-message'
                  className='margin-bottom-xxl'
                  type='info'
                  onClose={doCloseStatusMessage}
                  title={null}
                  variant={'flat'}
                  message={t(`statusMessage.messages.${messageFeature.message}`, 'Message', {
                    ...messageFeature.values,
                  })}
                />
              </div>
            )}
            <AiDataExtractionBanner />
            {!isNativeMobileView && (
              <>
                {shouldShowDraftListingDescription && (
                  <div
                    className={`${styles.draftListingDescription} margin-bottom-s`}
                    data-testid='draft-listing-description'
                  >
                    <span>{t('message.draftListing.description')}</span>
                    <PopoverContainer className='margin-left-s display-inline-flex'>
                      <PopoverTarget className='font-body-medium-regular align-left' data-testid='draft-listing-info'>
                        <IconS24InfoCircle24 style={{ color: '#757575' }} width={20} height={20} />
                      </PopoverTarget>
                      <PopoverBeta bodyPosition={'bottom'} arrowPosition={'left'} className={''}>
                        <div className={`display-flex gap-l padding-m ${styles.infoTooltipTextContainer}`}>
                          <span className={'font-body-medium-regular'}>{t('message.draftListing.infoIconText')}</span>
                        </div>
                      </PopoverBeta>
                    </PopoverContainer>
                  </div>
                )}
                <div
                  className={`font-body-default-regular padding-bottom-spacing-600 ${isWaitingListFlow ? 'padding-top-spacing-600' : ''} ${styles.mandatoryText}`}
                >
                  {t('message.mandatory')}
                </div>
              </>
            )}
            {content}
          </div>
        </div>
        <div className='grid-item sticky-publication-bar'>
          <PublicationStickyFooter
            isSaving={isSaving}
            isNativeMobileView={isNativeMobileView}
            isWaitingListFlow={isWaitingListFlow}
            onPreview={onPreview}
            onSave={onSave}
            onSaveForLater={onSaveForLater}
            increaseSubmitCount={increaseSubmitCount}
          />
        </div>
      </div>
    </>
  )
}

export default InsertionWizardPage
